import { useCallback, useMemo, useState } from 'react';
import { RType } from 'routes/types';

const STORAGE_KEY = 'command-palette-recent-pages';
const MAX_RECENT = 5;

interface RecentPage {
  path: string;
  name: string;
}

function loadRecent(): RecentPage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item: any) =>
        item && typeof item.path === 'string' && typeof item.name === 'string',
    );
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return [];
  }
}

function saveRecent(pages: RecentPage[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(pages));
}

export function useRecentPages(visibleRoutes: RType[]) {
  const [recent, setRecent] = useState<RecentPage[]>(loadRecent);

  const filteredRecent = useMemo(() => {
    const visiblePaths = new Set(
      visibleRoutes.map(r => r.layout + r.path),
    );
    return recent.filter(page => visiblePaths.has(page.path));
  }, [recent, visibleRoutes]);

  const addRecentPage = useCallback((path: string, name: string) => {
    setRecent(prev => {
      const filtered = prev.filter(p => p.path !== path);
      const next = [{ path, name }, ...filtered].slice(0, MAX_RECENT);
      saveRecent(next);
      return next;
    });
  }, []);

  return { recentPages: filteredRecent, addRecentPage };
}
